import React, { useState, useEffect, useRef } from 'react';
import AppSpinner from "../Spinner/AppSpinner";

import ConfirmModal from '../ConfirmModal/ConfirmModal';
import { BASE_URL } from '../../constants';
import { apiFetch } from "../../utils/apiFetch";
import axiosInstance from "../../utils/axiosInstance";

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileSelectInput = ({ selectedFile, onSelect }) => {
  const inputRef = useRef();

  return (
    <div>
      <div
        onClick={() => inputRef.current.click()}
        style={{
          border: `2px dashed ${selectedFile ? '#22c55e' : '#d1d5db'}`,
          borderRadius: 6, padding: '8px 10px',
          background: selectedFile ? '#f0fdf4' : '#fafafa',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 8,
          transition: 'all 0.15s'
        }}
      >
        {selectedFile ? (
          <>
            <svg viewBox="0 0 20 20" fill="none" style={{ width: 14, height: 14, flexShrink: 0 }}>
              <path d="M4 10l4 4 8-8" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span style={{ fontSize: 11, color: '#15803d', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selectedFile.name} {selectedFile.size ? `(${formatFileSize(selectedFile.size)})` : ''}
            </span>
            <span
              onClick={e => { e.stopPropagation(); onSelect(null); }}
              style={{ fontSize: 10, color: '#ef4444', cursor: 'pointer', flexShrink: 0, fontWeight: 700 }}
            >
              ✕
            </span>
          </>
        ) : (
          <>
            <svg viewBox="0 0 20 20" fill="none" style={{ width: 14, height: 14, flexShrink: 0 }}>
              <path d="M10 3v10M6 7l4-4 4 4M4 15h12" stroke="#9ca3af" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>Fayl seçmək üçün klikləyin</span>
          </>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={e => { onSelect(e.target.files[0] || null); e.target.value = ''; }}
      />
    </div>
  );
};

const LectureSlides = ({ lectureId, initialSlides = [], onRefresh, onShowToast }) => {
  const [slides, setSlides] = useState(initialSlides);
  const [selectedSlide, setSelectedSlide] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);

  const [previewSlide, setPreviewSlide] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewContentType, setPreviewContentType] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewFullscreen, setPreviewFullscreen] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const [showAddPanel, setShowAddPanel] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', sector: '' });
  const [addFile, setAddFile] = useState(null);
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');

  const [showEditPanel, setShowEditPanel] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', sector: '', fileUuidName: '' });
  const [editFile, setEditFile] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  useEffect(() => {
    setSlides(initialSlides);
    setSelectedSlide(null);
  }, [initialSlides]);

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiFetch(`${BASE_URL}/files/upload-lecture-slide`, {
      method: 'POST',
      body: formData
    });
    if (!response.ok) throw new Error('Fayl yüklənərkən xəta baş verdi.');
    const data = await response.json();
    return data.fileUuidName || data.fileEntity?.fileUuidName || '';
  };

  // DELETE — sadəcə massivdən çıxar, API-dən yenidən çəkmə
  const handleDelete = async () => {
    try {
      const res = await apiFetch(`${BASE_URL}/lecture-slides/${selectedSlide.id}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Silinərkən xəta baş verdi.');
      setSlides(prev => prev.filter(s => s.id !== selectedSlide.id));
      setSelectedSlide(null);
      setShowDeleteConfirm(false);
      onShowToast('Slayd uğurla silindi.', 'success');
    } catch (err) {
      setShowDeleteConfirm(false);
    }
  };

  // ADD — API-dən gələn yeni slaydı massivə push et
  const handleAdd = async () => {
    if (!addForm.name.trim()) { setAddError('Ad mütləqdir.'); return; }
    setAddLoading(true);
    setAddError('');
    try {
      let fileUuidName = '';
      if (addFile) {
        fileUuidName = await uploadFile(addFile);
      }
      const response = await apiFetch(`${BASE_URL}/lecture-slides`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: addForm.name, sector: addForm.sector, fileUuidName, lectureId })
      });
      if (!response.ok) {
        let msg = 'Əlavə edilərkən xəta baş verdi.';
        try { const d = await response.json(); msg = d.message || d.error || msg; } catch (_) {}
        throw new Error(msg);
      }
      const newSlide = await response.json();
      setSlides(prev => [...prev, newSlide]);
      setAddForm({ name: '', sector: '' });
      setAddFile(null);
      setShowAddPanel(false);
      onShowToast('Slayd uğurla əlavə edildi.', 'success');
    } catch (err) {
      setAddError(err.message || 'Əlavə edilərkən xəta baş verdi.');
    } finally {
      setAddLoading(false);
    }
  };

  const handleOpenEdit = () => {
    if (!selectedSlide) return;
    setShowAddPanel(false);
    setEditError('');
    setEditFile(null);
    setEditForm({ name: selectedSlide.name || '', sector: selectedSlide.sector || '', fileUuidName: selectedSlide.fileUuidName || '' });
    setShowEditPanel(true);
  };

  // EDIT — panel bağla, massivi yenilə, toast göstər
  const handleEdit = async () => {
    if (!editForm.name.trim()) { setEditError('Ad mütləqdir.'); return; }
    setEditLoading(true);
    setEditError('');
    try {
      let fileUuidName = editForm.fileUuidName;
      if (editFile) {
        fileUuidName = await uploadFile(editFile);
      }
      const response = await apiFetch(`${BASE_URL}/lecture-slides`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: selectedSlide.id, name: editForm.name, sector: editForm.sector, fileUuidName, lectureId })
      });
      if (!response.ok) {
        let msg = 'Redaktə edilərkən xəta baş verdi.';
        try { const d = await response.json(); msg = d.message || d.error || msg; } catch (_) {}
        throw new Error(msg);
      }
      // Massivi frontda yenilə — API-dən yenidən çəkmə
      const updatedSlide = { ...selectedSlide, name: editForm.name, sector: editForm.sector, fileUuidName };
      setSlides(prev => prev.map(s => s.id === selectedSlide.id ? updatedSlide : s));
      setSelectedSlide(null);
      setShowEditPanel(false);
      setEditFile(null);
      onShowToast('Slayd uğurla redaktə olundu.', 'success');
    } catch (err) {
      setEditError(err.message || 'Redaktə edilərkən xəta baş verdi.');
    } finally {
      setEditLoading(false);
    }
  };

  const handlePreview = async (slide, e) => {
    e.stopPropagation();
    if (!slide.fileUuidName) return;
    setPreviewSlide(slide);
    setPreviewUrl(null);
    setPreviewContentType('');
    setPreviewLoading(true);
    try {
      const isPdf = slide.fileUuidName.toLowerCase().endsWith('.pdf');
      const previewUrl = isPdf
        ? `${BASE_URL}/files/download-lecture-slide/${slide.fileUuidName}`
        : `${BASE_URL}/files/download-lecture-slide-pdf/${slide.fileUuidName.replace(/\.[^.]+$/, '')}.pdf`;
      const res = await axiosInstance.get(previewUrl, { responseType: 'blob' });
      const contentType = res.data.type || '';
      const url = URL.createObjectURL(res.data);
      setPreviewUrl(url);
      setPreviewContentType(contentType);
    } catch (_) {
      setPreviewUrl(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewSlide(null);
    setPreviewUrl(null);
    setPreviewContentType('');
    setPreviewFullscreen(false);
  };

  const handleDownload = async (slide, e) => {
    e.stopPropagation();
    if (!slide.fileUuidName || downloadingId === slide.id) return;
    setDownloadingId(slide.id);
    try {
      const res = await axiosInstance.get(`${BASE_URL}/files/download-lecture-slide/${slide.fileUuidName}`, {
        responseType: 'blob',
      });
      const blob = res.data;
      const disposition = res.headers?.['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)/i);
      const serverFilename = filenameMatch ? decodeURIComponent(filenameMatch[1].trim()) : null;
      const ext = slide.fileUuidName.includes('.') ? slide.fileUuidName.split('.').pop() : '';
      const baseName = (slide.name || slide.fileUuidName || 'fayl').replace(/\.[^.]+$/, '') + (ext ? '.' + ext : '');
      const downloadName = serverFilename || baseName;

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = downloadName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (_) {} finally {
      setDownloadingId(null);
    }
  };

  const handleCancelAdd = () => { setCancelTarget('add'); setShowCancelConfirm(true); };
  const handleCancelEdit = () => { setCancelTarget('edit'); setShowCancelConfirm(true); };
  const confirmCancel = () => {
    if (cancelTarget === 'add') { setShowAddPanel(false); setAddForm({ name: '', sector: '' }); setAddFile(null); setAddError(''); }
    if (cancelTarget === 'edit') { setShowEditPanel(false); setEditForm({ name: '', sector: '', fileUuidName: '' }); setEditFile(null); setEditError(''); }
    setShowCancelConfirm(false);
    setCancelTarget(null);
  };

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

        {/* ---- BAŞLIQ ---- */}
        <div style={{ background: '#f3f4f6', borderBottom: '1px solid #e5e7eb', padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontWeight: 700, fontSize: 11, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Slaydlar</span>
            {slides.length > 0 && (
              <span style={{ background: '#e0e7ff', color: '#4338ca', padding: '1px 7px', borderRadius: 10, fontSize: 10, fontWeight: 700 }}>{slides.length}</span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <button disabled={!selectedSlide} onClick={handleOpenEdit} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: selectedSlide ? '#3b82f6' : '#bfdbfe', color: '#fff', border: 'none', borderRadius: 5, padding: '4px 10px', fontWeight: 600, fontSize: 11, cursor: selectedSlide ? 'pointer' : 'not-allowed' }}>Redaktə et</button>
            <button disabled={!selectedSlide} onClick={() => setShowDeleteConfirm(true)} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: selectedSlide ? '#ef4444' : '#fecaca', color: '#fff', border: 'none', borderRadius: 5, padding: '4px 10px', fontWeight: 600, fontSize: 11, cursor: selectedSlide ? 'pointer' : 'not-allowed' }}>Sil</button>
            <button onClick={() => { setShowEditPanel(false); setShowAddPanel(p => !p); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: showAddPanel ? '#15803d' : '#22c55e', color: '#fff', border: 'none', borderRadius: 5, padding: '4px 10px', fontWeight: 600, fontSize: 11, cursor: 'pointer', transition: 'background 0.15s' }}>
              <svg viewBox="0 0 22 22" fill="white" xmlns="http://www.w3.org/2000/svg" style={{ width: 11, height: 11 }}>
                <path d="M11.75 8C11.75 7.586 11.414 7.25 11 7.25S10.25 7.586 10.25 8v2.25H8c-.414 0-.75.336-.75.75s.336.75.75.75h2.25V14c0 .414.336.75.75.75s.75-.336.75-.75v-2.25H14c.414 0 .75-.336.75-.75s-.336-.75-.75-.75h-2.25V8Z"/>
              </svg>
              Yeni slayd
            </button>
          </div>
        </div>

        {/* ---- ƏLAVƏ ET PANELİ ---- */}
        <div style={{ maxHeight: showAddPanel ? 260 : 0, overflow: 'hidden', transition: 'max-height 0.3s ease', flexShrink: 0 }}>
          <div style={{ background: '#f0fdf4', borderBottom: '2px solid #22c55e', padding: '12px 12px 10px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#15803d', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>+ Yeni Slayd</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <div style={{ flex: 2 }}>
                <label style={labelStyle}>Ad <span style={{ color: '#ef4444' }}>*</span></label>
                <input value={addForm.name} onChange={e => { setAddForm(p => ({ ...p, name: e.target.value })); setAddError(''); }} placeholder="Slayd adı" style={{ ...inputStyle, borderColor: addError && !addForm.name.trim() ? '#ef4444' : '#e5e7eb' }} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Sektor</label>
                <select value={addForm.sector} onChange={e => setAddForm(p => ({ ...p, sector: e.target.value }))} style={inputStyle}>
                  <option value="">Seçin</option>
                  <option value="Az">AZ</option>
                  <option value="Rus">RUS</option>
                  <option value="Eng">ENG</option>
                </select>
              </div>
            </div>
            <div style={{ marginBottom: 8 }}>
              <label style={labelStyle}>Fayl <span style={{ color: '#9ca3af', textTransform: 'none', fontWeight: 400 }}>(istəyə bağlı)</span></label>
              <FileSelectInput selectedFile={addFile} onSelect={setAddFile} />
            </div>
            {addError && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 5, padding: '4px 10px', color: '#dc2626', fontSize: 11, marginBottom: 8 }}>{addError}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
              <button onClick={handleCancelAdd} disabled={addLoading} style={{ background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 5, padding: '4px 12px', fontWeight: 600, fontSize: 11, cursor: 'pointer' }}>Ləğv et</button>
              <button onClick={handleAdd} disabled={addLoading} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: addLoading ? '#86efac' : '#22c55e', color: '#fff', border: 'none', borderRadius: 5, padding: '4px 14px', fontWeight: 600, fontSize: 11, cursor: addLoading ? 'not-allowed' : 'pointer' }}>
                {addLoading ? <><AppSpinner inline />{addFile ? 'Fayl yüklənir...' : 'Əlavə edilir...'}</> : 'Əlavə et'}
              </button>
            </div>
          </div>
        </div>

        {/* ---- REDAKTƏ PANELİ ---- */}
        <div style={{ maxHeight: showEditPanel ? 280 : 0, overflow: 'hidden', transition: 'max-height 0.3s ease', flexShrink: 0 }}>
          <div style={{ background: '#eff6ff', borderBottom: '2px solid #3b82f6', padding: '12px 12px 10px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Redaktə — {selectedSlide?.name}</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <div style={{ flex: 2 }}>
                <label style={labelStyle}>Ad <span style={{ color: '#ef4444' }}>*</span></label>
                <input value={editForm.name} onChange={e => { setEditForm(p => ({ ...p, name: e.target.value })); setEditError(''); }} placeholder="Slayd adı" style={{ ...inputStyle, borderColor: editError && !editForm.name.trim() ? '#ef4444' : '#e5e7eb' }} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Sektor</label>
                <select value={editForm.sector} onChange={e => setEditForm(p => ({ ...p, sector: e.target.value }))} style={inputStyle}>
                  <option value="">Seçin</option>
                  <option value="Az">AZ</option>
                  <option value="Rus">RUS</option>
                  <option value="Eng">ENG</option>
                </select>
              </div>
            </div>
            <div style={{ marginBottom: 8 }}>
              <label style={labelStyle}>
                Fayl
                {editForm.fileUuidName && !editFile && (
                  <span style={{ color: '#15803d', textTransform: 'none', fontWeight: 400, marginLeft: 4 }}>— mövcud fayl var</span>
                )}
                {editFile && (
                  <span style={{ color: '#d97706', textTransform: 'none', fontWeight: 400, marginLeft: 4 }}>— köhnə fayl əvəz olunacaq</span>
                )}
              </label>
              <FileSelectInput selectedFile={editFile} onSelect={setEditFile} />
            </div>
            {editError && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 5, padding: '4px 10px', color: '#dc2626', fontSize: 11, marginBottom: 8 }}>{editError}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
              <button onClick={handleCancelEdit} disabled={editLoading} style={{ background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 5, padding: '4px 12px', fontWeight: 600, fontSize: 11, cursor: 'pointer' }}>Ləğv et</button>
              <button onClick={handleEdit} disabled={editLoading} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: editLoading ? '#93c5fd' : '#3b82f6', color: '#fff', border: 'none', borderRadius: 5, padding: '4px 14px', fontWeight: 600, fontSize: 11, cursor: editLoading ? 'not-allowed' : 'pointer' }}>
                {editLoading ? <><AppSpinner inline />{editFile ? 'Fayl yüklənir...' : 'Yadda saxlanılır...'}</> : 'Yadda saxla'}
              </button>
            </div>
          </div>
        </div>

        {/* ---- SİYAHI ---- */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {slides.length > 0 ? (
            slides.map((slide, index) => {
              const isSelected = selectedSlide?.id === slide.id;
              return (
                <div
                  key={slide.id}
                  onClick={(e) => { setSelectedSlide(e.ctrlKey && isSelected ? null : slide); setShowEditPanel(false); }}
                  style={{ display: 'flex', alignItems: 'center', padding: '7px 12px', borderBottom: '1px solid #f3f4f6', background: isSelected ? '#eff6ff' : '#fff', cursor: 'pointer', transition: 'background 0.12s' }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f9fafb'; }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = '#fff'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                    <span style={{ background: '#f3f4f6', color: '#6b7280', padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 700, flexShrink: 0 }}>{index + 1}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 12, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {slide.name || `Slayd ${index + 1}`}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                        {slide.sector && <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '0px 5px', borderRadius: 3, fontSize: 9, fontWeight: 600, border: '1px solid #bfdbfe' }}>{slide.sector}</span>}
                        {slide.register && <span style={{ fontSize: 9, color: '#9ca3af' }}>{slide.register.split('T')[0]}</span>}
                        {slide.creatorUsername && <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '0px 5px', borderRadius: 3, fontSize: 9, fontWeight: 600, border: '1px solid #bfdbfe' }}>@{slide.creatorUsername}</span>}
                      </div>
                    </div>
                    {slide.fileUuidName && (
                      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                        <button
                          onClick={e => handlePreview(slide, e)}
                          title="Önizlə"
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            background: '#eff6ff', color: '#1d4ed8',
                            border: '1px solid #bfdbfe', borderRadius: 4,
                            padding: '3px 7px', fontSize: 10, fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          <svg viewBox="0 0 20 20" fill="none" style={{ width: 11, height: 11 }}>
                            <path d="M1 10s3.5-7 9-7 9 7 9 7-3.5 7-9 7-9-7-9-7Z" stroke="#1d4ed8" strokeWidth="1.6"/>
                            <circle cx="10" cy="10" r="2.5" stroke="#1d4ed8" strokeWidth="1.6"/>
                          </svg>
                          Bax
                        </button>
                        <button
                          onClick={e => handleDownload(slide, e)}
                          disabled={downloadingId === slide.id}
                          title="Faylı yüklə"
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            background: downloadingId === slide.id ? '#e0f2fe' : '#f0f9ff',
                            color: '#0369a1',
                            border: '1px solid #bae6fd', borderRadius: 4,
                            padding: '3px 7px', fontSize: 10, fontWeight: 600,
                            cursor: downloadingId === slide.id ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {downloadingId === slide.id ? (
                            <><AppSpinner inline /> Yüklənir...</>
                          ) : (
                            <>
                              <svg viewBox="0 0 20 20" fill="none" style={{ width: 11, height: 11 }}>
                                <path d="M10 3v10M6 13l4 4 4-4M4 17h12" stroke="#0369a1" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              Yüklə
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '50px 16px', color: '#9ca3af', textAlign: 'center' }}>
              <i className="bi bi-file-earmark-image" style={{ fontSize: 30, marginBottom: 8, display: 'block', color: '#d1d5db' }}></i>
              <p style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', margin: '0 0 4px' }}>Hələ ki slayd yoxdur</p>
              <p style={{ fontSize: 10, color: '#9ca3af', margin: 0 }}>Yeni slayd əlavə etmək üçün yuxarıdakı düyməni istifadə edin</p>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Silməni Təsdiqlə"
        message={<><strong>{selectedSlide?.name}</strong> adlı slaydı silmək istədiyinizə əminsiniz?</>}
        confirmText="Bəli, Sil"
      />
      <ConfirmModal
        show={showCancelConfirm}
        onHide={() => { setShowCancelConfirm(false); setCancelTarget(null); }}
        onConfirm={confirmCancel}
        title="Ləğv et"
        message="Daxil edilmiş məlumatlar itəcək. Davam etmək istəyirsiniz?"
        confirmText="Bəli, Ləğv et"
      />

      {/* ---- ÖNİZLƏMƏ MODALI ---- */}
      {previewSlide && (
        <div
          onClick={handleClosePreview}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: previewFullscreen ? 0 : 10,
              width: previewFullscreen ? '100vw' : '88vw',
              height: previewFullscreen ? '100vh' : '88vh',
              display: 'flex', flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: previewFullscreen ? 'none' : '0 8px 40px rgba(0,0,0,0.3)',
              transition: 'width 0.2s, height 0.2s, border-radius 0.2s',
            }}
          >
            {/* Modal header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 16px', borderBottom: '1px solid #e5e7eb',
              background: '#f9fafb', flexShrink: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <svg viewBox="0 0 20 20" fill="none" style={{ width: 14, height: 14 }}>
                  <path d="M1 10s3.5-7 9-7 9 7 9 7-3.5 7-9 7-9-7-9-7Z" stroke="#1d4ed8" strokeWidth="1.6"/>
                  <circle cx="10" cy="10" r="2.5" stroke="#1d4ed8" strokeWidth="1.6"/>
                </svg>
                <span style={{ fontWeight: 700, fontSize: 13, color: '#111827' }}>{previewSlide.name}</span>
                {previewSlide.sector && (
                  <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600, border: '1px solid #bfdbfe' }}>{previewSlide.sector}</span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  onClick={e => handleDownload(previewSlide, e)}
                  disabled={downloadingId === previewSlide.id}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: downloadingId === previewSlide.id ? '#e0f2fe' : '#f0f9ff',
                    color: '#0369a1',
                    border: '1px solid #bae6fd', borderRadius: 5,
                    padding: '4px 12px', fontSize: 11, fontWeight: 600,
                    cursor: downloadingId === previewSlide.id ? 'not-allowed' : 'pointer',
                  }}
                >
                  {downloadingId === previewSlide.id ? (
                    <><AppSpinner inline /> Yüklənir...</>
                  ) : (
                    <>
                      <svg viewBox="0 0 20 20" fill="none" style={{ width: 11, height: 11 }}>
                        <path d="M10 3v10M6 13l4 4 4-4M4 17h12" stroke="#0369a1" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Yüklə
                    </>
                  )}
                </button>
                <button
                  onClick={() => setPreviewFullscreen(f => !f)}
                  title={previewFullscreen ? 'Kiçilt' : 'Tam ekran'}
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 28, height: 28, borderRadius: 6,
                    background: '#f0fdf4', color: '#16a34a',
                    border: '1px solid #bbf7d0', cursor: 'pointer', fontSize: 13,
                  }}
                >
                  <i className={`bi ${previewFullscreen ? 'bi-fullscreen-exit' : 'bi-fullscreen'}`} />
                </button>
                <button
                  onClick={handleClosePreview}
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 28, height: 28, borderRadius: 6,
                    background: '#fee2e2', color: '#ef4444',
                    border: '1px solid #fecaca', cursor: 'pointer', fontSize: 14, fontWeight: 700,
                  }}
                >✕</button>
              </div>
            </div>

            {/* Modal content */}
            <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f3f4f6' }}>
              {previewLoading ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, color: '#6b7280' }}>
                  <AppSpinner text="Fayl yüklənir..." />
                </div>
              ) : !previewUrl ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, color: '#9ca3af' }}>
                  <i className="bi bi-exclamation-circle" style={{ fontSize: 32 }}></i>
                  <span style={{ fontSize: 13 }}>Fayl yüklənmədi</span>
                </div>
              ) : previewContentType.includes('pdf') ? (
                <iframe
                  src={previewUrl}
                  title={previewSlide.name}
                  style={{ width: '100%', height: '100%', border: 'none' }}
                />
              ) : previewContentType.startsWith('image/') ? (
                <img
                  src={previewUrl}
                  alt={previewSlide.name}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 4 }}
                />
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, color: '#6b7280', textAlign: 'center', padding: 32 }}>
                  <i className="bi bi-file-earmark-text" style={{ fontSize: 48, color: '#d1d5db' }}></i>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Bu fayl növü birbaşa önizlənmir</span>
                  <span style={{ fontSize: 12, color: '#9ca3af' }}>Faylı yükləyib açın</span>
                  <button
                    onClick={e => handleDownload(previewSlide, e)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      background: '#0369a1', color: '#fff',
                      border: 'none', borderRadius: 6,
                      padding: '7px 18px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    <svg viewBox="0 0 20 20" fill="none" style={{ width: 13, height: 13 }}>
                      <path d="M10 3v10M6 13l4 4 4-4M4 17h12" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Yüklə
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const labelStyle = {
  display: 'block', fontSize: 10, fontWeight: 700,
  color: '#6b7280', textTransform: 'uppercase',
  letterSpacing: '0.04em', marginBottom: 3
};

const inputStyle = {
  width: '100%', border: '1px solid #e5e7eb',
  borderRadius: 5, padding: '5px 8px',
  fontSize: 11, color: '#111827', background: '#fff',
  outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit'
};

export default LectureSlides;
